/**
 * Workshop events calendar — filtered list with date range + type pills.
 * Fetch: section data-wec-fetch-* lat/lng/radius + start_date/end_date query params.
 */
(function () {
  'use strict';

  var TYPE_ORDER = ['mosaic', 'ceramic', 'resin'];
  var MONTH_NAMES = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December'
  ];

  var VENUE_PALETTE = [
    { bg: '#e8f4f8', border: '#9ec5d4', text: '#1a4a5c' },
    { bg: '#f5ebe0', border: '#d4b896', text: '#5c3d1a' },
    { bg: '#ede9fe', border: '#b8a9f0', text: '#4c1d95' },
    { bg: '#ecfdf5', border: '#9fd9bf', text: '#14532d' },
    { bg: '#fce7f3', border: '#f0a8d0', text: '#831843' },
    { bg: '#fff7ed', border: '#f5c48a', text: '#7c2d12' },
    { bg: '#f0f9ff', border: '#9bd5f5', text: '#0c4a6e' },
    { bg: '#f7fee7', border: '#c8e6a0', text: '#365314' }
  ];

  function pad2(n) {
    return n < 10 ? '0' + n : String(n);
  }

  function escHtml(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  function escAttr(s) {
    return escHtml(s).replace(/"/g, '&quot;');
  }

  function parseConfig(root) {
    var el = root.querySelector('[data-wec-config]');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent || '{}');
    } catch (_) {
      return null;
    }
  }

  function getEventStart(ev) {
    return (ev && (ev.start_time || ev.starts_at || ev.date || ev.startsAt)) || '';
  }

  function getEventDateKey(ev) {
    if (!ev) return '';
    var dateKey = (ev.date_key || '').toString().trim();
    if (dateKey) return dateKey.slice(0, 10);
    var startRaw = getEventStart(ev);
    if (!startRaw) return '';
    var m = String(startRaw).match(/^(\d{4}-\d{2}-\d{2})/);
    return m ? m[1] : '';
  }

  function parseDateKey(dateKey) {
    if (!dateKey) return null;
    var parts = String(dateKey).split('-');
    if (parts.length !== 3) return null;
    var y = Number(parts[0]);
    var m = Number(parts[1]) - 1;
    var d = Number(parts[2]);
    if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return null;
    return new Date(y, m, d);
  }

  function toDateKey(dateObj) {
    if (!(dateObj instanceof Date) || Number.isNaN(dateObj.getTime())) return '';
    return dateObj.getFullYear() + '-' + pad2(dateObj.getMonth() + 1) + '-' + pad2(dateObj.getDate());
  }

  function formatWallClockTime(raw) {
    if (!raw) return '';
    var s = String(raw).trim();
    var t = s.indexOf('T');
    if (t !== -1) {
      var rest = s.slice(t + 1);
      var m = rest.match(/^(\d{1,2}):(\d{2})/);
      if (m) {
        var hh = parseInt(m[1], 10);
        var mm = parseInt(m[2], 10);
        var h12 = hh % 12 || 12;
        var ap = hh < 12 ? 'AM' : 'PM';
        return h12 + ':' + pad2(mm) + ' ' + ap;
      }
    }
    var m2 = s.match(/(\d{4}-\d{2}-\d{2})\s+(\d{1,2}):(\d{2})/);
    if (m2) {
      var hh2 = parseInt(m2[2], 10);
      var mm2 = parseInt(m2[3], 10);
      var h122 = hh2 % 12 || 12;
      var ap2 = hh2 < 12 ? 'AM' : 'PM';
      return h122 + ':' + pad2(mm2) + ' ' + ap2;
    }
    return '';
  }

  function formatDisplayDate(dateKey) {
    var d = parseDateKey(dateKey);
    if (!d) return '';
    return pad2(d.getMonth() + 1) + '/' + pad2(d.getDate()) + '/' + d.getFullYear();
  }

  function isValidDateKey(dateKey) {
    var d = parseDateKey(dateKey);
    if (!d) return false;
    var y = d.getFullYear();
    return y >= 2000 && y <= 2100;
  }

  function parseFlexibleDate(raw) {
    var s = (raw == null ? '' : String(raw)).trim();
    if (!s) return '';
    var iso = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
    if (iso) {
      var isoKey = iso[1] + '-' + pad2(Number(iso[2])) + '-' + pad2(Number(iso[3]));
      return isValidDateKey(isoKey) ? isoKey : '';
    }
    var slash = s.match(/^(\d{1,2})[\/.\-](\d{1,2})[\/.\-](\d{4})$/);
    if (slash) {
      var slashKey =
        slash[3] + '-' + pad2(Number(slash[1])) + '-' + pad2(Number(slash[2]));
      return isValidDateKey(slashKey) ? slashKey : '';
    }
    var d = parseDateKey(s);
    if (d) {
      var key = toDateKey(d);
      return isValidDateKey(key) ? key : '';
    }
    return '';
  }

  function venueColorForKey(key) {
    var h = 0;
    var s = String(key || '');
    for (var i = 0; i < s.length; i++) {
      h = ((h << 5) - h + s.charCodeAt(i)) | 0;
    }
    return VENUE_PALETTE[Math.abs(h) % VENUE_PALETTE.length];
  }

  function getVenueClusterKey(venue) {
    var v = venue || {};
    return [
      (v.handle || v.venue_handle || '').toString().trim(),
      (v.venue_name || '').toString().trim(),
      (v.venue_address || '').toString().trim()
    ].join('|');
  }

  function clusterByVenue(eventGroups) {
    var map = new Map();
    eventGroups.forEach(function (item) {
      var key = getVenueClusterKey(item.venue);
      if (!map.has(key)) {
        map.set(key, { key: key, venue: item.venue || {}, cards: [] });
      }
      map.get(key).cards.push(item);
    });
    var clusters = Array.from(map.values());
    clusters.forEach(function (cluster) {
      cluster.cards.sort(function (a, b) {
        return String(a.dateKey || '').localeCompare(String(b.dateKey || ''));
      });
    });
    clusters.sort(function (a, b) {
      var da = (a.cards[0] && a.cards[0].dateKey) || '';
      var db = (b.cards[0] && b.cards[0].dateKey) || '';
      return da.localeCompare(db);
    });
    return clusters;
  }

  function formatMonth(d) {
    if (!d) return '';
    return d.toLocaleString('en', { month: 'short' }).toUpperCase();
  }

  function formatDay(d) {
    return d ? d.getDate() : '--';
  }

  function formatMonthYear(dateObj) {
    if (!(dateObj instanceof Date) || Number.isNaN(dateObj.getTime())) return '';
    return dateObj.toLocaleString('en-US', { month: 'long', year: 'numeric' });
  }

  function ensureCalendarCursor(cursor, fallbackDate) {
    if (cursor instanceof Date && !Number.isNaN(cursor.getTime())) {
      return new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    }
    var seed =
      fallbackDate instanceof Date && !Number.isNaN(fallbackDate.getTime())
        ? fallbackDate
        : new Date();
    return new Date(seed.getFullYear(), seed.getMonth(), 1);
  }

  function getCalendarYearBounds() {
    var current = new Date().getFullYear();
    return { min: current - 1, max: current + 3 };
  }

  function getWorkshopTypeKey(item, productTypes) {
    var gid = item && item.product_gid;
    if (gid && productTypes && productTypes[gid]) return productTypes[gid];
    var name = (item && item.product_name ? String(item.product_name) : '').toLowerCase();
    if (name.indexOf('mosaic') !== -1) return 'mosaic';
    if (name.indexOf('ceramic') !== -1) return 'ceramic';
    if (name.indexOf('resin') !== -1) return 'resin';
    return '';
  }

  function matchesTypeFilter(item, productTypes, activeTypes) {
    var typeKey = getWorkshopTypeKey(item, productTypes);
    if (!typeKey) return activeTypes.size === TYPE_ORDER.length;
    return activeTypes.has(typeKey);
  }

  function extractNumericEventId(val) {
    if (val == null) return '';
    var s = String(val).trim();
    if (!s) return '';
    var metaGid = /^gid:\/\/shopify\/Metaobject\/(\d+)$/i.exec(s);
    if (metaGid) return metaGid[1];
    if (/^\d+$/.test(s)) return s;
    return '';
  }

  function buildBookUrl(productUrl, eventId, venueHandle) {
    var base = (productUrl || '').trim();
    if (!base) return '#';
    try {
      var u = new URL(base, window.location.origin);
      var eid = extractNumericEventId(eventId);
      if (eid) u.searchParams.set('event_id', eid);
      if (venueHandle) u.searchParams.set('venue', venueHandle);
      return u.pathname + u.search;
    } catch (_) {
      var qs = [];
      var eid2 = extractNumericEventId(eventId);
      if (eid2) qs.push('event_id=' + encodeURIComponent(eid2));
      if (venueHandle) qs.push('venue=' + encodeURIComponent(venueHandle));
      return base + (base.indexOf('?') > -1 ? '&' : '?') + qs.join('&');
    }
  }

  function coerceImageUrl(img) {
    if (img == null || img === '') return '';
    if (typeof img === 'object') {
      if (img.url) return String(img.url).trim();
      if (img.src) return String(img.src).trim();
    }
    return String(img).trim();
  }

  function normalizeVenueNameKey(name) {
    return String(name || '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, ' ')
      .replace(/[^\w\s\u0100-\u024f-]/g, '');
  }

  function venueImageLookupKeys(venue) {
    var v = venue && typeof venue === 'object' ? venue : {};
    var keys = [];
    var seen = Object.create(null);
    function pushKey(raw) {
      var s = String(raw == null ? '' : raw).trim();
      if (!s || seen[s]) return;
      seen[s] = true;
      keys.push(s);
    }
    function pushRef(raw) {
      var s = String(raw == null ? '' : raw).trim();
      if (!s) return;
      pushKey(s);
      var gidMatch = s.match(/Metaobject\/(\d+)/i);
      if (gidMatch) pushKey('gid://shopify/Metaobject/' + gidMatch[1]);
      if (/^\d+$/.test(s)) pushKey('gid://shopify/Metaobject/' + s);
      var tail = s.split('/').pop();
      if (tail && tail !== s) pushKey(tail);
    }
    pushRef(v.id);
    pushRef(v.venue_ref_id);
    pushRef(v.venue_metaobject_gid);
    var handle = (v.handle || v.venue_handle || '').toString().trim().toLowerCase();
    if (handle) pushKey(handle);
    var nameKey = normalizeVenueNameKey(v.venue_name || v.name || '');
    if (nameKey) pushKey(nameKey);
    var cityKey = normalizeVenueNameKey(v.city_name || v.city || '');
    if (cityKey) pushKey(cityKey);
    return keys;
  }

  function getVenueImageUrl(venue, venueImages) {
    var v = venue && typeof venue === 'object' ? venue : {};
    var url = coerceImageUrl(
      v.venue_image ||
        v.venueImage ||
        v.venue_image_url ||
        v.image ||
        v.image_url ||
        v.cover_image ||
        ''
    );
    if (url) return url;
    var images = venueImages && typeof venueImages === 'object' ? venueImages : {};
    var keys = venueImageLookupKeys(v);
    for (var i = 0; i < keys.length; i++) {
      if (images[keys[i]]) return images[keys[i]];
    }
    return '';
  }

  function renderVenueMedia(venue, venueImages) {
    var url = getVenueImageUrl(venue, venueImages);
    var name = ((venue && venue.venue_name) || 'Venue').toString();
    if (url) {
      return (
        '<div class="wec-event-card__media">' +
        '<img src="' +
        escAttr(url) +
        '" alt="' +
        escAttr(name) +
        '" loading="lazy" decoding="async">' +
        '</div>'
      );
    }
    return (
      '<div class="wec-event-card__media wec-event-card__media--empty" aria-hidden="true">' +
      '<svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">' +
      '<rect x="3" y="3" width="18" height="18" rx="2"/>' +
      '<circle cx="8.5" cy="8.5" r="1.5" fill="currentColor" stroke="none"/>' +
      '<path d="M21 15l-5-5L5 21"/>' +
      '</svg>' +
      '</div>'
    );
  }

  function formatVenueMeta(item) {
    var venue = (item && item.venue) || {};
    var parts = [];
    var city = (venue.city || '').toString().trim();
    var state = (venue.state || venue.state_code || '').toString().trim();
    var loc = [city, state].filter(Boolean).join(', ');
    if (loc) parts.push(loc);
    return parts.join(' • ');
  }

  function resolveFetchContext(root, config) {
    var lat = Number(root && root.getAttribute('data-wec-fetch-lat'));
    var lng = Number(root && root.getAttribute('data-wec-fetch-lng'));
    var radiusKm = Number(root && root.getAttribute('data-wec-fetch-radius-km'));

    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      lat = Number(config && config.fallbackLat);
      lng = Number(config && config.fallbackLng);
    }
    if (!Number.isFinite(radiusKm) || radiusKm <= 0) {
      radiusKm = Number(config && config.fallbackRadiusKm);
    }

    if (lat === 43 && lng === -75) {
      lat = 39.833333;
      lng = -98.583333;
    }
    if (!Number.isFinite(radiusKm) || radiusKm <= 1000) {
      radiusKm = 2650;
    }

    return { lat: lat, lng: lng, radiusKm: radiusKm, label: '' };
  }

  function fetchEventsForGid(gid, lat, lng, radiusKm, proxyBase, eventsPath, productNames, rangeStart, rangeEnd) {
    var shared = typeof window !== 'undefined' ? window.AMCWorkshopEventsV : null;
    var req = {
      productGid: gid,
      lat: lat,
      lng: lng,
      radiusKm: radiusKm,
      includeEmpty: false,
      proxyBase: proxyBase,
      eventsPath: eventsPath
    };
    var load =
      shared && typeof shared.get === 'function'
        ? shared.get(req)
        : fetch(
            proxyBase +
              eventsPath +
              '?product_gid=' +
              encodeURIComponent(gid) +
              '&lat=' +
              lat +
              '&lng=' +
              lng +
              '&radius_km=' +
              radiusKm
          ).then(function (r) {
            return r.json();
          });
    return load
      .then(function (data) {
        if (!data || !data.ok || !data.groups) return [];
        var events = [];
        data.groups.forEach(function (group) {
          (group.venues || []).forEach(function (venue) {
            (venue.events || []).forEach(function (ev) {
              var eventSelectionId = (
                ev.event_metaobject_gid ||
                ev.eventMetaobjectGid ||
                ev.metaobject_gid ||
                ev.id ||
                ev.event_id ||
                ''
              ).toString();
              events.push({
                event: ev,
                venue: venue,
                product_gid: gid,
                product_name: (productNames && productNames[gid]) || 'Workshop',
                event_selection_id: eventSelectionId
              });
            });
          });
        });
        return events;
      })
      .catch(function () {
        return [];
      });
  }

  function getEventGroups(items) {
    var map = new Map();
    items.forEach(function (item) {
      var ev = item.event || {};
      var venue = item.venue || {};
      var dk = getEventDateKey(ev);
      var startRaw = getEventStart(ev);
      if (!dk || !startRaw) return;
      var groupKey = [item.product_gid || '', venue.venue_name || '', venue.venue_address || '', dk].join('|');
      if (!map.has(groupKey)) {
        map.set(groupKey, {
          product_gid: item.product_gid || '',
          product_name: item.product_name || 'Workshop',
          venue: venue,
          dateObj: parseDateKey(dk),
          dateKey: dk,
          times: []
        });
      }
      map.get(groupKey).times.push({
        eventId: (item.event_selection_id || '').toString(),
        startRaw: startRaw,
        label: formatWallClockTime(startRaw),
        price: ev.price_display || ev.price || '',
        eventHandle: (ev.handle || ev.event_handle || '').toString().trim(),
        venueHandle: (venue.handle || venue.venue_handle || '').toString().trim()
      });
    });

    var groups = Array.from(map.values());
    groups.forEach(function (g) {
      g.times.sort(function (a, b) {
        return String(a.startRaw || '').localeCompare(String(b.startRaw || ''));
      });
      var first = g.times[0] || {};
      g.selectedEventId = first.eventId || '';
      g.selectedStartRaw = first.startRaw || '';
      g.selectedPrice = first.price || '';
      g.selectedVenueHandle = first.venueHandle || '';
    });
    groups.sort(function (a, b) {
      return String(a.selectedStartRaw || '').localeCompare(String(b.selectedStartRaw || ''));
    });
    return groups;
  }

  function initSection(root) {
    if (!root || root.__wecBound) return;

    var config = parseConfig(root) || {};
    var gids = Array.isArray(config.gids) ? config.gids.filter(Boolean) : [];
    var productNames = config.productNames || {};
    var productUrls = config.productUrls || {};
    var productTypes = config.productTypes || {};
    var venueImages = config.venueImages || {};
    var proxyBase = config.proxyBase || '/apps/workshop-events';
    var eventsPath = config.eventsPath || '/events-v';
    var citiesPerRow = Math.max(1, Number(config.citiesPerRow) || 2);
    var rowsPerPage = Math.max(1, Number(config.rowsPerPage) || 3);
    var mobileRowsPerPage = Math.max(1, Number(config.mobileRowsPerPage) || 3);
    var citiesPerPage = Math.max(
      1,
      Number(config.citiesPerPage) || rowsPerPage * citiesPerRow
    );
    var cardsPerVenue = Math.max(1, Number(config.cardsPerVenue) || 2);

    var heroEl = root.querySelector('.wec-hero');
    var filtersEl = root.querySelector('[data-wec-filters]');
    var startInput = root.querySelector('[data-wec-start-input]');
    var endInput = root.querySelector('[data-wec-end-input]');
    var calPopup = root.querySelector('[data-wec-cal-popup]');
    var calOpenBtn = root.querySelector('[data-wec-cal-open]');
    var calendarEl = root.querySelector('[data-wec-calendar]');
    var listEl = root.querySelector('[data-wec-list]');
    var paginationEl = root.querySelector('[data-wec-pagination]');

    var allEvents = [];
    var activeTypes = new Set(TYPE_ORDER.slice());
    var rangeStart = '';
    var rangeEnd = '';
    var calendarCursor = null;
    var currentCityPage = 1;
    var venueCarouselOffsets = {};
    var mobileListMinHeight = 0;
    var isFetching = false;

    function totalCityPages(cityCount) {
      return Math.max(1, Math.ceil(cityCount / getActiveCitiesPerPage()));
    }

    function resetCityPagination() {
      currentCityPage = 1;
      resetMobileListHeightLock();
    }

    function getActiveCitiesPerPage() {
      var mobile = typeof window !== 'undefined' && window.matchMedia('(max-width: 749px)').matches;
      if (mobile) return mobileRowsPerPage;
      return citiesPerPage;
    }

    function isMobileViewport() {
      return typeof window !== 'undefined' && window.matchMedia('(max-width: 749px)').matches;
    }

    function resetMobileListHeightLock() {
      mobileListMinHeight = 0;
      if (listEl) listEl.style.minHeight = '';
    }

    function updateMobileListHeightLock() {
      if (!listEl) return;
      if (!isMobileViewport()) {
        listEl.style.minHeight = '';
        return;
      }
      var nextHeight = Math.ceil(listEl.getBoundingClientRect().height);
      if (!nextHeight) return;
      if (nextHeight > mobileListMinHeight) mobileListMinHeight = nextHeight;
      if (mobileListMinHeight > 0) listEl.style.minHeight = mobileListMinHeight + 'px';
    }

    function resetVenueCarouselOffsets() {
      venueCarouselOffsets = {};
    }

    function clampVenueCarouselOffset(clusterKey, cardCount) {
      var offset = venueCarouselOffsets[clusterKey] || 0;
      var maxOffset = Math.max(0, cardCount - cardsPerVenue);
      if (offset > maxOffset) offset = maxOffset;
      venueCarouselOffsets[clusterKey] = offset;
      return offset;
    }

    function defaultRange() {
      var today = new Date();
      today.setHours(0, 0, 0, 0);
      var end = new Date(today);
      end.setDate(end.getDate() + 90);
      rangeStart = toDateKey(today);
      rangeEnd = toDateKey(end);
      calendarCursor = new Date(today.getFullYear(), today.getMonth(), 1);
    }

    function updateDateDisplays() {
      if (startInput) {
        startInput.value = rangeStart ? formatDisplayDate(rangeStart) : '';
      }
      if (endInput) {
        endInput.value = rangeEnd ? formatDisplayDate(rangeEnd) : '';
      }
    }

    function normalizeDateRange() {
      if (!rangeStart) {
        rangeEnd = '';
        return;
      }
      if (rangeStart && rangeEnd && rangeEnd < rangeStart) {
        rangeEnd = rangeStart;
      }
    }

    function applyDateInputs(triggerFetch) {
      if (startInput) {
        if (!startInput.value.trim()) rangeStart = '';
        else {
          var startParsed = parseFlexibleDate(startInput.value);
          if (startParsed) rangeStart = startParsed;
        }
      }
      if (endInput) {
        if (!endInput.value.trim()) rangeEnd = '';
        else {
          var endParsed = parseFlexibleDate(endInput.value);
          if (endParsed) rangeEnd = endParsed;
        }
      }
      normalizeDateRange();
      updateDateDisplays();
      if (triggerFetch) fetchAll();
      else if (allEvents.length) renderList();
    }

    function closeCalPopup() {
      if (calPopup) calPopup.hidden = true;
      if (calOpenBtn) calOpenBtn.classList.remove('is-open');
    }

    function positionCalPopup(anchorBtn) {
      if (!calPopup || !anchorBtn || !heroEl) return;
      var heroRect = heroEl.getBoundingClientRect();
      var btnRect = anchorBtn.getBoundingClientRect();
      var panel = calPopup.querySelector('.wec-cal-popup__panel');
      var panelWidth = (panel && panel.offsetWidth) || 300;
      var panelHeight = (panel && panel.offsetHeight) || 320;
      var gutter = 8;
      var mobile = window.matchMedia('(max-width: 749px)').matches;

      var desiredLeft = btnRect.left - heroRect.left;
      if (mobile) {
        desiredLeft = btnRect.right - heroRect.left - panelWidth;
      }
      var minLeft = gutter;
      var maxLeft = Math.max(minLeft, heroRect.width - panelWidth - gutter);
      var left = Math.min(maxLeft, Math.max(minLeft, desiredLeft));

      var topBelow = btnRect.bottom - heroRect.top + 8;
      var topAbove = btnRect.top - heroRect.top - panelHeight - 8;
      var top = topBelow;
      if (btnRect.bottom + panelHeight > window.innerHeight - 8 && topAbove >= gutter) {
        top = topAbove;
      }

      calPopup.style.left = Math.max(gutter, left) + 'px';
      calPopup.style.top = Math.max(gutter, top) + 'px';
    }

    function openCalPopup(anchorBtn) {
      var seed = parseDateKey(rangeStart) || parseDateKey(rangeEnd) || new Date();
      calendarCursor = ensureCalendarCursor(null, seed);
      if (calPopup) calPopup.hidden = false;
      if (anchorBtn) anchorBtn.classList.add('is-open');
      renderCalendar();
      positionCalPopup(anchorBtn);
    }

    function isInRange(dateKey) {
      if (!dateKey) return false;
      if (rangeStart && dateKey < rangeStart) return false;
      if (rangeEnd && dateKey > rangeEnd) return false;
      return true;
    }

    function dayInRangeVisual(dateKey) {
      if (!rangeStart || !rangeEnd || !dateKey) return false;
      return dateKey >= rangeStart && dateKey <= rangeEnd;
    }

    function renderCalendar() {
      if (!calendarEl) return;
      var seed = parseDateKey(rangeStart) || parseDateKey(rangeEnd) || new Date();
      calendarCursor = ensureCalendarCursor(calendarCursor, seed);
      var year = calendarCursor.getFullYear();
      var month = calendarCursor.getMonth();
      var firstDay = new Date(year, month, 1);
      var lastDay = new Date(year, month + 1, 0);
      var leading = (firstDay.getDay() + 6) % 7;
      var daysInMonth = lastDay.getDate();
      if (!Number.isFinite(daysInMonth) || daysInMonth < 1) {
        calendarCursor = ensureCalendarCursor(null, new Date());
        year = calendarCursor.getFullYear();
        month = calendarCursor.getMonth();
        firstDay = new Date(year, month, 1);
        lastDay = new Date(year, month + 1, 0);
        leading = (firstDay.getDay() + 6) % 7;
        daysInMonth = lastDay.getDate();
      }
      var totalSlots = Math.ceil((leading + daysInMonth) / 7) * 7;
      var weekdays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
      var todayKey = toDateKey(new Date());
      var yearBounds = getCalendarYearBounds();

      var monthOptions = MONTH_NAMES.map(function (name, idx) {
        return (
          '<option value="' +
          idx +
          '"' +
          (idx === month ? ' selected' : '') +
          '>' +
          escHtml(name) +
          '</option>'
        );
      }).join('');

      var yearOptions = '';
      for (var y = yearBounds.min; y <= yearBounds.max; y++) {
        yearOptions +=
          '<option value="' + y + '"' + (y === year ? ' selected' : '') + '>' + y + '</option>';
      }

      var dayHtml = '';
      for (var i = 0; i < totalSlots; i++) {
        if (i < leading || i >= leading + daysInMonth) {
          dayHtml += '<div class="wec-cal-cell--empty" aria-hidden="true"></div>';
          continue;
        }
        var dayNum = i - leading + 1;
        var d = new Date(year, month, dayNum);
        var dk = toDateKey(d);
        var cls = 'wec-cal-day';
        var isPast = dk < todayKey;
        if (isPast) cls += ' wec-cal-day--muted';
        if (dayInRangeVisual(dk)) cls += ' wec-cal-day--in-range';
        if (rangeStart && dk === rangeStart) cls += ' wec-cal-day--range-start';
        if (rangeEnd && dk === rangeEnd) cls += ' wec-cal-day--range-end';
        if (rangeStart && !rangeEnd && dk === rangeStart) cls += ' wec-cal-day--selected';
        dayHtml +=
          '<button type="button" class="' +
          cls +
          '" data-wec-cal-day="' +
          escAttr(dk) +
          '"' +
          (isPast ? ' data-wec-past="true" tabindex="-1"' : '') +
          '>' +
          dayNum +
          '</button>';
      }

      calendarEl.innerHTML =
        '<div class="wec-cal-header">' +
        '<button type="button" class="wec-cal-nav" data-wec-cal-nav="prev" aria-label="Previous month">&lt;</button>' +
        '<div class="wec-cal-controls">' +
        '<select class="wec-cal-select" data-wec-cal-month aria-label="Month">' +
        monthOptions +
        '</select>' +
        '<select class="wec-cal-select" data-wec-cal-year aria-label="Year">' +
        yearOptions +
        '</select>' +
        '</div>' +
        '<button type="button" class="wec-cal-nav" data-wec-cal-nav="next" aria-label="Next month">&gt;</button>' +
        '</div>' +
        '<div class="wec-cal-weekdays">' +
        weekdays
          .map(function (w) {
            return '<span class="wec-cal-weekday">' + w + '</span>';
          })
          .join('') +
        '</div>' +
        '<div class="wec-cal-grid">' +
        dayHtml +
        '</div>';
    }

    function onCalendarDayClick(dateKey) {
      if (!dateKey) return;
      if (!rangeStart || (rangeStart && rangeEnd)) {
        rangeStart = dateKey;
        rangeEnd = '';
        normalizeDateRange();
        updateDateDisplays();
        renderCalendar();
        return;
      }
      if (dateKey < rangeStart) {
        rangeStart = dateKey;
        rangeEnd = '';
        updateDateDisplays();
        renderCalendar();
        return;
      }
      rangeEnd = dateKey;
      normalizeDateRange();
      updateDateDisplays();
      closeCalPopup();
      fetchAll();
    }

    function syncFilterPills() {
      if (!filtersEl) return;
      var allSelected = activeTypes.size === TYPE_ORDER.length;
      filtersEl.querySelectorAll('[data-wec-filter]').forEach(function (btn) {
        var key = (btn.getAttribute('data-wec-filter') || '').toLowerCase();
        var on = key === 'all' ? allSelected : !allSelected && activeTypes.has(key);
        btn.classList.toggle('is-active', on);
        btn.setAttribute('aria-selected', on ? 'true' : 'false');
      });
    }

    function setupMobileFilterHint() {
      if (!filtersEl || typeof window === 'undefined' || !('IntersectionObserver' in window)) return;
      if (window.matchMedia('(min-width: 750px)').matches) return;
      if (filtersEl.__wecHintBound) return;
      filtersEl.__wecHintBound = true;

      var hasOverflow = filtersEl.scrollWidth - filtersEl.clientWidth > 8;
      if (!hasOverflow) return;

      var isHintPlaying = false;
      var lastHintAt = 0;
      function playHint() {
        if (isHintPlaying) return;
        var now = Date.now();
        if (now - lastHintAt < 2200) return;
        lastHintAt = now;
        isHintPlaying = true;
        filtersEl.classList.remove('is-scroll-hint');
        void filtersEl.offsetWidth;
        filtersEl.classList.add('is-scroll-hint');
        window.setTimeout(function () {
          filtersEl.classList.remove('is-scroll-hint');
          isHintPlaying = false;
        }, 1300);
      }

      // Run once shortly after render so users immediately notice horizontal scroll.
      window.setTimeout(playHint, 350);

      var obs = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            playHint();
          });
        },
        { threshold: 0.35 }
      );
      obs.observe(root);
    }

    function setLoading(on) {
      if (!listEl) return;
      if (on) {
        resetMobileListHeightLock();
        listEl.innerHTML =
          '<div class="wec-loading"><div class="wec-spinner"></div><p>' +
          escHtml(config.loadingLabel || 'Finding workshops…') +
          '</p></div>';
      }
    }

    function fetchAll() {
      if (isFetching) return Promise.resolve();
      if (!gids.length) {
        if (listEl) {
          listEl.innerHTML =
            '<div class="wec-empty">' +
            escHtml(config.noCollectionLabel || 'Choose a workshop collection in section settings.') +
            '</div>';
        }
        return Promise.resolve();
      }
      var ctx = resolveFetchContext(root, config);
      if (!Number.isFinite(ctx.lat) || !Number.isFinite(ctx.lng)) {
        if (listEl) {
          listEl.innerHTML =
            '<div class="wec-empty">' +
            escHtml(config.noLocationLabel || 'Set your location in the booking wizard to see nearby workshops.') +
            '</div>';
        }
        return Promise.resolve();
      }
      isFetching = true;
      setLoading(true);
      return Promise.all(
        gids.map(function (gid) {
          return fetchEventsForGid(
            gid,
            ctx.lat,
            ctx.lng,
            ctx.radiusKm,
            proxyBase,
            eventsPath,
            productNames,
            rangeStart,
            rangeEnd
          );
        })
      )
        .then(function (results) {
          allEvents = [].concat.apply([], results);
          allEvents.sort(function (a, b) {
            return String(getEventStart(a.event) || '').localeCompare(String(getEventStart(b.event) || ''));
          });
          resetCityPagination();
          resetVenueCarouselOffsets();
          renderList();
        })
        .catch(function () {
          if (listEl) {
            listEl.innerHTML =
              '<div class="wec-empty">' + escHtml(config.errorLabel || "Couldn't load sessions. Try again.") + '</div>';
          }
        })
        .finally(function () {
          isFetching = false;
        });
    }

    function renderList() {
      if (!listEl) return;
      var filtered = allEvents.filter(function (item) {
        var dk = getEventDateKey(item.event);
        return matchesTypeFilter(item, productTypes, activeTypes) && isInRange(dk);
      });

      var grouped = getEventGroups(filtered);
      var allVenueClusters = clusterByVenue(grouped);
      if (!allVenueClusters.length) {
        resetMobileListHeightLock();
        listEl.innerHTML =
          '<div class="wec-empty">' +
          escHtml(config.emptyLabel || 'No workshops found for this date range. Try different dates or filters.') +
          '</div>';
        if (paginationEl) {
          paginationEl.innerHTML = '';
          paginationEl.hidden = true;
        }
        return;
      }

      var totalPages = totalCityPages(allVenueClusters.length);
      var activeCitiesPerPage = getActiveCitiesPerPage();
      if (currentCityPage > totalPages) currentCityPage = totalPages;
      var pageStart = (currentCityPage - 1) * activeCitiesPerPage;
      var venueClusters = allVenueClusters.slice(pageStart, pageStart + activeCitiesPerPage);

      function renderEventCard(item, hidden) {
        var d = item.dateObj;
        var price = item.selectedPrice || '';
        var eventSelectionId = (item.selectedEventId || '').toString();
        var timesArr = (item.times || []).slice(0, 4);
        var activeSlot = timesArr[0] || {};
        var venueHandle = (activeSlot.venueHandle || '').toString();
        var bookUrl = buildBookUrl(productUrls[item.product_gid] || '', eventSelectionId, venueHandle);

        var timesClass =
          timesArr.length > 2 ? ' wec-event-card__times wec-event-card__times--multi' : ' wec-event-card__times';
        var timesHtml =
          '<div class="' +
          timesClass.trim() +
          '" data-wec-event-times>' +
          timesArr
            .map(function (slot, idx) {
              var activeCls = idx === 0 ? ' is-active' : '';
              return (
                '<button type="button" class="wec-event-time-btn' +
                activeCls +
                '" data-time-event-id="' +
                escAttr(slot.eventId || '') +
                '" data-time-price="' +
                escAttr(slot.price || '') +
                '" data-time-venue="' +
                escAttr(slot.venueHandle || '') +
                '">' +
                escHtml(slot.label || '') +
                '</button>'
              );
            })
            .join('') +
          '</div>';

        var footerHtml = price
          ? '<div class="wec-event-card__footer"><span class="wec-event-card__price">' +
            escHtml(price) +
            '</span></div>'
          : '';

        return (
          '<a class="wec-event-card"' +
          (hidden ? ' hidden' : '') +
          ' href="' +
          escAttr(bookUrl) +
          '" data-wec-event-card data-product-url="' +
          escAttr(productUrls[item.product_gid] || '') +
          '" data-event-id="' +
          escAttr(eventSelectionId) +
          '">' +
          '<div class="wec-event-card__body">' +
          '<div class="wec-event-card__date">' +
          '<span class="wec-event-card__date-day">' +
          formatDay(d) +
          '</span>' +
          '<span class="wec-event-card__date-month">' +
          formatMonth(d) +
          '</span>' +
          '</div>' +
          '<p class="wec-event-card__type">' +
          escHtml(item.product_name || 'Workshop') +
          '</p>' +
          timesHtml +
          footerHtml +
          '</div>' +
          renderVenueMedia(item.venue, venueImages) +
          '</a>'
        );
      }

      function renderVenueCarousel(cluster) {
        var cards = cluster.cards || [];
        var isPassive = cards.length <= cardsPerVenue;
        var offset = isPassive ? 0 : clampVenueCarouselOffset(cluster.key, cards.length);
        var maxOffset = Math.max(0, cards.length - cardsPerVenue);
        var cardsHtml = cards
          .map(function (item, idx) {
            var isVisible = isPassive || (idx >= offset && idx < offset + cardsPerVenue);
            return renderEventCard(item, !isVisible);
          })
          .join('');

        var navPrev =
          '<button type="button" class="wec-venue-nav wec-venue-nav--prev' +
          (isPassive ? ' is-passive' : '') +
          '" data-wec-venue-prev data-cluster-key="' +
          escAttr(cluster.key) +
          '" aria-label="Previous sessions"' +
          (isPassive || offset <= 0 ? ' disabled' : '') +
          '><span aria-hidden="true">&lsaquo;</span></button>';

        var navNext =
          '<button type="button" class="wec-venue-nav wec-venue-nav--next' +
          (isPassive ? ' is-passive' : '') +
          '" data-wec-venue-next data-cluster-key="' +
          escAttr(cluster.key) +
          '" aria-label="Next sessions"' +
          (isPassive || offset >= maxOffset ? ' disabled' : '') +
          '><span aria-hidden="true">&rsaquo;</span></button>';

        return (
          '<div class="wec-venue-group__carousel">' +
          navPrev +
          '<div class="wec-venue-group__grid">' +
          cardsHtml +
          '</div>' +
          navNext +
          '</div>'
        );
      }

      listEl.innerHTML =
        '<div class="wec-venue-groups">' +
        venueClusters
          .map(function (cluster) {
            var colors = venueColorForKey(cluster.key);
            var venue = cluster.venue || {};
            var venueMeta = formatVenueMeta({ venue: venue });
            return (
              '<section class="wec-venue-group" style="--wec-card-bg:' +
              escAttr(colors.bg) +
              ';--wec-card-border:' +
              escAttr(colors.border) +
              ';--wec-card-text:' +
              escAttr(colors.text) +
              '">' +
              '<header class="wec-venue-group__head">' +
              '<h3 class="wec-venue-group__title">' +
              escHtml(venue.venue_name || 'Venue') +
              '</h3>' +
              (venueMeta ? '<span class="wec-venue-group__meta">' + escHtml(venueMeta) + '</span>' : '') +
              '</header>' +
              renderVenueCarousel(cluster) +
              '</section>'
            );
          })
          .join('') +
        '</div>';
      updateMobileListHeightLock();

      listEl.querySelectorAll('.wec-event-time-btn').forEach(function (btn) {
        btn.addEventListener('click', function (e) {
          e.preventDefault();
          e.stopPropagation();
          var card = btn.closest('[data-wec-event-card]');
          if (!card) return;
          card.querySelectorAll('.wec-event-time-btn').forEach(function (other) {
            other.classList.remove('is-active');
          });
          btn.classList.add('is-active');
          var eid = btn.getAttribute('data-time-event-id') || '';
          var vh = btn.getAttribute('data-time-venue') || '';
          var purl = card.getAttribute('data-product-url') || '';
          card.setAttribute('data-event-id', eid);
          card.setAttribute('href', buildBookUrl(purl, eid, vh));
          var price = btn.getAttribute('data-time-price') || '';
          var priceEl = card.querySelector('.wec-event-card__price');
          if (priceEl) priceEl.textContent = price;
        });
      });

      if (paginationEl) {
        if (allVenueClusters.length <= activeCitiesPerPage) {
          paginationEl.innerHTML = '';
          paginationEl.hidden = true;
        } else {
          paginationEl.hidden = false;
          var pageHtml =
            '<div class="wec-pagination-inner" role="navigation" aria-label="City pages">';
          for (var p = 1; p <= totalPages; p++) {
            var isActive = p === currentCityPage;
            pageHtml +=
              '<button type="button" class="wec-page-btn' +
              (isActive ? ' is-active' : '') +
              '" data-wec-city-page="' +
              p +
              '"' +
              (isActive ? ' aria-current="page"' : '') +
              '>' +
              p +
              '</button>';
          }
          pageHtml += '</div>';
          paginationEl.innerHTML = pageHtml;
        }
      }
    }

    if (paginationEl && !paginationEl.__wecCityPageBound) {
      paginationEl.__wecCityPageBound = true;
      paginationEl.addEventListener('click', function (e) {
        var pageBtn = e.target.closest('[data-wec-city-page]');
        if (!pageBtn) return;
        e.preventDefault();
        var page = Number(pageBtn.getAttribute('data-wec-city-page')) || 1;
        if (page === currentCityPage) return;
        currentCityPage = page;
        resetVenueCarouselOffsets();
        renderList();
        if (listEl && typeof listEl.scrollIntoView === 'function') {
          listEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      });
    }

    if (listEl && !listEl.__wecCarouselBound) {
      listEl.__wecCarouselBound = true;
      listEl.addEventListener('click', function (e) {
        var prevBtn = e.target.closest('[data-wec-venue-prev]');
        var nextBtn = e.target.closest('[data-wec-venue-next]');
        if (!prevBtn && !nextBtn) return;
        e.preventDefault();
        var btn = prevBtn || nextBtn;
        if (btn.disabled) return;
        var key = btn.getAttribute('data-cluster-key') || '';
        if (!key) return;
        venueCarouselOffsets[key] = Math.max(0, (venueCarouselOffsets[key] || 0) + (prevBtn ? -1 : 1));
        renderList();
      });
    }

    if (startInput) {
      startInput.addEventListener('keydown', function (e) {
        if (e.key === 'Enter') {
          e.preventDefault();
          applyDateInputs(true);
        }
      });
      startInput.addEventListener('blur', function () {
        applyDateInputs(true);
      });
    }
    if (endInput) {
      endInput.addEventListener('keydown', function (e) {
        if (e.key === 'Enter') {
          e.preventDefault();
          applyDateInputs(true);
        }
      });
      endInput.addEventListener('blur', function () {
        applyDateInputs(true);
      });
    }

    root.addEventListener('change', function (e) {
      if (!calendarEl || !calPopup || calPopup.hidden) return;
      if (e.target.matches('[data-wec-cal-month]')) {
        calendarCursor = ensureCalendarCursor(
          new Date(calendarCursor.getFullYear(), Number(e.target.value), 1),
          new Date()
        );
        renderCalendar();
        return;
      }
      if (e.target.matches('[data-wec-cal-year]')) {
        calendarCursor = ensureCalendarCursor(
          new Date(Number(e.target.value), calendarCursor.getMonth(), 1),
          new Date()
        );
        renderCalendar();
      }
    });

    root.addEventListener('click', function (e) {
      var calOpenBtn = e.target.closest('[data-wec-cal-open]');
      if (calOpenBtn) {
        e.stopPropagation();
        if (calPopup && !calPopup.hidden) {
          closeCalPopup();
        } else {
          openCalPopup(calOpenBtn);
        }
        return;
      }

      var pill = e.target.closest('[data-wec-filter]');
      if (pill && filtersEl && filtersEl.contains(pill)) {
        closeCalPopup();
        pill.classList.add('is-tapped');
        window.setTimeout(function () {
          pill.classList.remove('is-tapped');
        }, 240);
        var key = (pill.getAttribute('data-wec-filter') || '').toLowerCase();
        if (key === 'all') {
          activeTypes = new Set(TYPE_ORDER.slice());
        } else if (activeTypes.size === TYPE_ORDER.length) {
          activeTypes = new Set([key]);
        } else if (activeTypes.has(key) && activeTypes.size === 1) {
          activeTypes = new Set(TYPE_ORDER.slice());
        } else {
          activeTypes = new Set([key]);
        }
        syncFilterPills();
        resetCityPagination();
        renderList();
        return;
      }

      var nav = e.target.closest('[data-wec-cal-nav]');
      if (nav && calendarEl && calendarEl.contains(nav)) {
        e.preventDefault();
        e.stopPropagation();
        var dir = nav.getAttribute('data-wec-cal-nav') === 'prev' ? -1 : 1;
        if (!calendarCursor) calendarCursor = new Date();
        calendarCursor = ensureCalendarCursor(
          new Date(calendarCursor.getFullYear(), calendarCursor.getMonth() + dir, 1),
          new Date()
        );
        renderCalendar();
        return;
      }

      var dayBtn = e.target.closest('[data-wec-cal-day]');
      if (dayBtn && calendarEl && calendarEl.contains(dayBtn) && !dayBtn.classList.contains('wec-cal-day--muted')) {
        e.stopPropagation();
        onCalendarDayClick(dayBtn.getAttribute('data-wec-cal-day'));
        return;
      }

      if (calPopup && !calPopup.hidden && !calPopup.contains(e.target)) {
        closeCalPopup();
      }
    });

    root.__wecBound = true;

    defaultRange();
    updateDateDisplays();
    syncFilterPills();
    setupMobileFilterHint();
    fetchAll();

    if (typeof window !== 'undefined') {
      var mobileMq = window.matchMedia('(max-width: 749px)');
      var lastMobileLayout = mobileMq.matches;
      function onViewportLayoutChange() {
        var isMobile = mobileMq.matches;
        if (isMobile === lastMobileLayout) return;
        lastMobileLayout = isMobile;
        resetCityPagination();
        renderList();
      }
      if (typeof mobileMq.addEventListener === 'function') {
        mobileMq.addEventListener('change', onViewportLayoutChange);
      } else if (typeof mobileMq.addListener === 'function') {
        mobileMq.addListener(onViewportLayoutChange);
      }
    }

    if (typeof window !== 'undefined') {
      window.__WEC_refetch = function () {
        return fetchAll();
      };
    }
  }

  function boot(evt) {
    var scope = evt && evt.target ? evt.target : document;
    scope.querySelectorAll('[data-workshop-events-calendar]').forEach(function (root) {
      if (evt && evt.type === 'shopify:section:load') {
        root.__wecBound = false;
      }
      initSection(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      boot();
    });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', boot);
})();
